<template>
  <div class="html-editor">
    <div
      class="html-editor__toolbar d-flex flex-wrap align-center pa-1"
      role="toolbar"
      aria-label="Text formatieren"
    >
      <v-btn
        v-for="action in actions"
        :key="action.command"
        icon
        x-small
        :title="action.label"
        :aria-label="action.label"
        :disabled="disabled"
        @mousedown.prevent
        @click="runCommand(action.command)"
      >
        <v-icon small>
          {{ action.icon }}
        </v-icon>
      </v-btn>
      <v-divider vertical class="mx-1"></v-divider>
      <v-btn
        icon
        x-small
        title="Link einfügen"
        aria-label="Link einfügen"
        :disabled="disabled"
        @mousedown.prevent
        @click="openLinkInput"
      >
        <v-icon small>
          mdi-link-variant-plus
        </v-icon>
      </v-btn>
      <v-btn
        icon
        x-small
        title="Formatierung entfernen"
        aria-label="Formatierung entfernen"
        :disabled="disabled"
        @mousedown.prevent
        @click="runCommand('removeFormat')"
      >
        <v-icon small>
          mdi-format-clear
        </v-icon>
      </v-btn>
      <v-btn
        icon
        x-small
        title="Rückgängig"
        aria-label="Rückgängig"
        :disabled="disabled"
        @mousedown.prevent
        @click="runCommand('undo')"
      >
        <v-icon small>
          mdi-undo
        </v-icon>
      </v-btn>
    </div>

    <v-expand-transition>
      <div
        v-if="linkInputVisible"
        class="html-editor__link d-flex align-center pa-2"
      >
        <v-text-field
          ref="linkInput"
          v-model="linkUrl"
          dense
          outlined
          hide-details
          label="Link-Adresse"
          placeholder="https://example.org"
          :disabled="disabled"
          @keydown.enter.prevent="applyLink"
          @keydown.esc.prevent="closeLinkInput"
        />
        <v-btn
          small
          color="primary"
          class="ml-2"
          :disabled="disabled || !linkUrl.trim()"
          @click="applyLink"
        >
          Einfügen
        </v-btn>
        <v-btn
          icon
          small
          class="ml-1"
          title="Abbrechen"
          aria-label="Link-Eingabe abbrechen"
          @click="closeLinkInput"
        >
          <v-icon small>
            mdi-close
          </v-icon>
        </v-btn>
      </div>
    </v-expand-transition>

    <div
      ref="editor"
      class="html-editor__content pa-3"
      :class="{ 'html-editor__content--disabled': disabled }"
      :contenteditable="!disabled"
      :data-placeholder="placeholder"
      role="textbox"
      aria-multiline="true"
      :aria-label="label"
      @input="emitValue"
      @blur="emitValue"
      @keyup="rememberSelection"
      @mouseup="rememberSelection"
      @paste="handlePaste"
      @dragover.prevent
      @drop.prevent="handleDrop"
    ></div>

    <div
      v-if="allowImages"
      class="text-caption text--secondary mt-1"
    >
      Bilder können mit Strg+V eingefügt werden (PNG, JPEG, GIF oder WebP,
      maximal {{ maxImageSizeLabel }} je Bild).
    </div>
  </div>
</template>

<script>
const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];
const ALLOWED_TAGS = new Set([
  'p', 'br', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr',
  'td', 'th', 'hr', 'h1', 'h2', 'h3', 'blockquote',
]);
const DANGEROUS_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button',
  'meta', 'link', 'svg', 'math',
]);
const MAX_IMAGES = 8;
const MAX_TOTAL_IMAGE_BYTES = 4 * 1024 * 1024;
const SAFE_STYLE_PROPERTIES = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'font-weight',
  'font-style', 'text-decoration', 'text-align', 'line-height', 'width', 'height',
  'max-width', 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border', 'border-collapse', 'vertical-align',
]);

export default {
  name: 'HtmlEditor',
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'HTML-Texteditor',
    },
    placeholder: {
      type: String,
      default: 'Text eingeben ...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowImages: {
      type: Boolean,
      default: true,
    },
    maxImageBytes: {
      type: Number,
      default: 1024 * 1024,
    },
  },
  data: () => ({
    actions: [
      { command: 'bold', icon: 'mdi-format-bold', label: 'Fett' },
      { command: 'italic', icon: 'mdi-format-italic', label: 'Kursiv' },
      { command: 'underline', icon: 'mdi-format-underline', label: 'Unterstrichen' },
      { command: 'insertUnorderedList', icon: 'mdi-format-list-bulleted', label: 'Aufzählung' },
      { command: 'insertOrderedList', icon: 'mdi-format-list-numbered', label: 'Nummerierte Liste' },
    ],
    linkInputVisible: false,
    linkUrl: '',
    savedRange: null,
    pendingImageCount: 0,
    pendingImageBytes: 0,
  }),
  computed: {
    maxImageSizeLabel() {
      const megabytes = this.maxImageBytes / (1024 * 1024);
      return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
    },
  },
  watch: {
    value() {
      this.syncValue();
    },
  },
  mounted() {
    this.syncValue();
  },
  methods: {
    syncValue() {
      const { editor } = this.$refs;
      if (editor) {
        const safeValue = this.sanitizeForEditor(this.value || '');
        if (editor.innerHTML !== safeValue) editor.innerHTML = safeValue;
      }
    },
    sanitizeForEditor(value) {
      if (typeof document === 'undefined') return '';
      const parsed = new DOMParser().parseFromString(String(value || ''), 'text/html');
      const state = { imageCount: 0, imageBytes: 0 };
      const sanitizeNode = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName.toLowerCase();
        if (DANGEROUS_TAGS.has(tag)) {
          node.remove();
          return;
        }
        if (!ALLOWED_TAGS.has(tag)) {
          Array.from(node.children).forEach(sanitizeNode);
          const parent = node.parentNode;
          if (!parent) return;
          while (node.firstChild) parent.insertBefore(node.firstChild, node);
          parent.removeChild(node);
          return;
        }

        const allowedAttributes = new Set(['title', 'style']);
        if (tag === 'a') allowedAttributes.add('href');
        if (tag === 'img') ['src', 'alt', 'width', 'height'].forEach((name) => allowedAttributes.add(name));
        if (tag === 'td' || tag === 'th') ['colspan', 'rowspan'].forEach((name) => allowedAttributes.add(name));
        Array.from(node.attributes).forEach((attribute) => {
          if (!allowedAttributes.has(attribute.name.toLowerCase())) node.removeAttribute(attribute.name);
        });

        if (node.hasAttribute('style')) {
          const safeStyle = this.sanitizeEditorStyle(node.getAttribute('style'));
          if (safeStyle) node.setAttribute('style', safeStyle);
          else node.removeAttribute('style');
        }

        if (tag === 'a' && node.hasAttribute('href')) {
          const href = node.getAttribute('href').trim();
          if (!/^(https?:\/\/|mailto:)/i.test(href) || /[\r\n\0]/.test(href)) {
            node.removeAttribute('href');
          } else {
            node.setAttribute('rel', 'noopener noreferrer');
          }
        }
        if (tag === 'img') {
          if (!this.allowImages) {
            node.remove();
            return;
          }
          const source = node.getAttribute('src') || '';
          const match = source.match(/^data:image\/(png|jpe?g|gif|webp);base64,([A-Za-z0-9+/=\s]+)$/i);
          const imageBytes = match
            ? Math.max(0, Math.floor(match[2].replace(/\s+/g, '').length * 3 / 4)) : 0;
          state.imageCount += 1;
          state.imageBytes += imageBytes;
          if (!match || imageBytes < 1 || imageBytes > this.maxImageBytes
            || state.imageCount > MAX_IMAGES || state.imageBytes > MAX_TOTAL_IMAGE_BYTES) {
            node.remove();
            return;
          }
          ['width', 'height'].forEach((dimension) => {
            if (!node.hasAttribute(dimension)) return;
            const numericValue = Number(node.getAttribute(dimension));
            if (!Number.isInteger(numericValue) || numericValue < 1 || numericValue > 2000) {
              node.removeAttribute(dimension);
            }
          });
        }
        Array.from(node.children).forEach(sanitizeNode);
      };
      Array.from(parsed.body.children).forEach(sanitizeNode);
      return parsed.body.innerHTML;
    },
    sanitizeEditorStyle(style) {
      return String(style || '').split(';').map((declaration) => {
        const separator = declaration.indexOf(':');
        if (separator <= 0) return '';
        const property = declaration.slice(0, separator).trim().toLowerCase();
        const value = declaration.slice(separator + 1).trim();
        const lowerValue = value.toLowerCase();
        if (!SAFE_STYLE_PROPERTIES.has(property) || !value || value.length > 100
          || /url|expression|javascript|@/.test(lowerValue) || /[<>\\]/.test(value)) return '';
        return `${property}: ${value}`;
      }).filter(Boolean).join('; ');
    },
    emitValue() {
      const { editor } = this.$refs;
      if (!editor) {
        return;
      }
      this.$emit('input', editor.innerHTML);
      this.rememberSelection();
    },
    focusEditor() {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
    },
    runCommand(command) {
      if (this.disabled) {
        return;
      }
      this.focusEditor();
      document.execCommand(command, false, null);
      this.emitValue();
    },
    rememberSelection() {
      const { editor } = this.$refs;
      const selection = window.getSelection ? window.getSelection() : null;
      if (!editor || !selection || selection.rangeCount <= 0) {
        return;
      }
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        this.savedRange = range.cloneRange();
      }
    },
    restoreSelection() {
      const selection = window.getSelection ? window.getSelection() : null;
      if (!selection || !this.savedRange) {
        this.focusEditor();
        return;
      }
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    },
    openLinkInput() {
      if (this.disabled) {
        return;
      }
      this.rememberSelection();
      this.linkUrl = '';
      this.linkInputVisible = true;
      this.$nextTick(() => {
        if (this.$refs.linkInput) {
          this.$refs.linkInput.focus();
        }
      });
    },
    closeLinkInput() {
      this.linkInputVisible = false;
      this.linkUrl = '';
      this.$nextTick(this.focusEditor);
    },
    normalizeLink(value) {
      const link = value.trim();
      if (/^(https?:\/\/|mailto:)/i.test(link)) {
        return link;
      }
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(link)) {
        return `mailto:${link}`;
      }
      return `https://${link}`;
    },
    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    applyLink() {
      if (this.disabled || !this.linkUrl.trim()) {
        return;
      }
      const link = this.normalizeLink(this.linkUrl);
      this.restoreSelection();
      const selection = window.getSelection ? window.getSelection() : null;
      if (selection && selection.rangeCount > 0 && selection.getRangeAt(0).collapsed) {
        const safeLink = this.escapeHtml(link);
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${safeLink}">${safeLink}</a>`,
        );
      } else {
        document.execCommand('createLink', false, link);
      }
      this.linkInputVisible = false;
      this.linkUrl = '';
      this.emitValue();
    },
    emitImageError(message) {
      this.$emit('error', message);
    },
    handlePaste(event) {
      if (this.disabled || !event.clipboardData) {
        return;
      }
      event.preventDefault();
      this.handleTransfer(event.clipboardData);
    },
    handleDrop(event) {
      if (this.disabled || !event.dataTransfer) return;
      this.rememberDropPosition(event);
      this.handleTransfer(event.dataTransfer);
    },
    rememberDropPosition(event) {
      let range = null;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(event.clientX, event.clientY);
      } else if (document.caretPositionFromPoint) {
        const position = document.caretPositionFromPoint(event.clientX, event.clientY);
        if (position) {
          range = document.createRange();
          range.setStart(position.offsetNode, position.offset);
          range.collapse(true);
        }
      }
      if (range && this.$refs.editor && this.$refs.editor.contains(range.commonAncestorContainer)) {
        this.savedRange = range.cloneRange();
      }
    },
    handleTransfer(transfer) {
      const transferItems = transfer.items ? Array.from(transfer.items) : [];
      const imageItems = transferItems
        .filter((item) => item.kind === 'file' && item.type.indexOf('image/') === 0);
      if (imageItems.length <= 0) {
        this.insertPlainText(transfer.getData('text/plain'));
        return;
      }
      if (!this.allowImages) {
        this.emitImageError('Bilder sind in diesem Textbereich nicht erlaubt.');
        return;
      }
      this.rememberSelection();
      const files = imageItems.map((item) => item.getAsFile()).filter(Boolean);
      if (files.length !== imageItems.length) {
        this.emitImageError('Mindestens ein Bild konnte nicht gelesen werden.');
        return;
      }
      if (files.some((file) => !ALLOWED_IMAGE_TYPES.includes(file.type))) {
        this.emitImageError('Es können nur PNG-, JPEG-, GIF- oder WebP-Bilder eingefügt werden.');
        return;
      }
      if (files.some((file) => file.size > this.maxImageBytes)) {
        this.emitImageError(`Mindestens ein Bild ist größer als ${this.maxImageSizeLabel}.`);
        return;
      }
      const existing = this.currentImageStats();
      const addedBytes = files.reduce((sum, file) => sum + file.size, 0);
      if (existing.count + this.pendingImageCount + files.length > MAX_IMAGES
        || existing.bytes + this.pendingImageBytes + addedBytes > MAX_TOTAL_IMAGE_BYTES) {
        this.emitImageError('Es sind höchstens 8 Bilder mit zusammen 4 MB erlaubt.');
        return;
      }
      this.pendingImageCount += files.length;
      this.pendingImageBytes += addedBytes;
      files.forEach((file) => this.readAndInsertImage(file));
    },
    currentImageStats() {
      const editor = this.$refs.editor;
      if (!editor) return { count: 0, bytes: 0 };
      return Array.from(editor.querySelectorAll('img[src^="data:image/"]'))
        .reduce((stats, image) => {
          const payload = (image.getAttribute('src') || '').split(',', 2)[1] || '';
          return {
            count: stats.count + 1,
            bytes: stats.bytes + Math.max(0, Math.floor(payload.replace(/\s+/g, '').length * 3 / 4)),
          };
        }, { count: 0, bytes: 0 });
    },
    insertPlainText(text) {
      if (!text) {
        return;
      }
      this.rememberSelection();
      this.restoreSelection();
      const inserted = document.execCommand('insertText', false, text);
      if (!inserted) {
        const selection = window.getSelection ? window.getSelection() : null;
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const fragment = document.createDocumentFragment();
          let lastNode = null;
          text.split(/\r?\n/).forEach((line, index, lines) => {
            lastNode = document.createTextNode(line);
            fragment.appendChild(lastNode);
            if (index < lines.length - 1) {
              lastNode = document.createElement('br');
              fragment.appendChild(lastNode);
            }
          });
          range.deleteContents();
          range.insertNode(fragment);
          if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            this.savedRange = range.cloneRange();
          }
        }
      }
      this.emitValue();
    },
    readAndInsertImage(file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.pendingImageCount = Math.max(0, this.pendingImageCount - 1);
        this.pendingImageBytes = Math.max(0, this.pendingImageBytes - file.size);
        if (typeof reader.result !== 'string' || !reader.result.startsWith('data:image/')) {
          this.emitImageError('Das Bild aus der Zwischenablage konnte nicht verarbeitet werden.');
          return;
        }
        this.insertImage(reader.result);
      };
      reader.onerror = () => {
        this.pendingImageCount = Math.max(0, this.pendingImageCount - 1);
        this.pendingImageBytes = Math.max(0, this.pendingImageBytes - file.size);
        this.emitImageError('Das Bild aus der Zwischenablage konnte nicht gelesen werden.');
      };
      reader.readAsDataURL(file);
    },
    insertImage(dataUrl) {
      if (!this.allowImages) return;
      const { editor } = this.$refs;
      if (!editor) {
        return;
      }
      this.restoreSelection();
      const selection = window.getSelection ? window.getSelection() : null;
      const image = document.createElement('img');
      image.src = dataUrl;
      image.alt = 'Eingefügtes Bild';
      image.style.maxWidth = '100%';
      image.style.height = 'auto';

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(image);
        range.setStartAfter(image);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        this.savedRange = range.cloneRange();
      } else {
        editor.appendChild(image);
      }
      this.emitValue();
    },
  },
};
</script>

<style scoped>
.html-editor {
  width: 100%;
}

.html-editor__toolbar,
.html-editor__link {
  border: 1px solid rgba(0, 0, 0, 0.24);
  background: rgba(0, 0, 0, 0.03);
}

.html-editor__toolbar {
  border-bottom: 0;
  border-radius: 4px 4px 0 0;
}

.html-editor__link {
  border-bottom: 0;
}

.html-editor__content {
  min-height: 220px;
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 0 0 4px 4px;
  background: #fff;
  color: rgba(0, 0, 0, 0.87);
  outline: none;
}

.html-editor__content:focus {
  border-color: var(--v-primary-base);
  box-shadow: inset 0 0 0 1px var(--v-primary-base);
}

.html-editor__content:empty::before {
  color: rgba(0, 0, 0, 0.54);
  content: attr(data-placeholder);
  pointer-events: none;
}

.html-editor__content--disabled {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.5);
  cursor: not-allowed;
}
</style>
